import {
  type ColumnOrderState,
  functionalUpdate,
  type Updater,
} from '@tanstack/react-table';
import { isEqual } from 'lodash-es';
import { useCallback, useLayoutEffect, useRef } from 'react';

import { useCallbackRef } from '../../../../core/hooks/useCallbackRef.js';
import { useControllableState } from '../../../../core/hooks/useControllableState.js';
import type { DataTableColumnDef } from '../../public.api.js';
import type { DataTableRowData } from '../../row-data-types.js';
import type { DataTableOptions } from '../types/table.types.js';
import type { DataTableColumnOrderProps } from './column-order-types.js';
import {
  extractColumnOrderAndGroups,
  validateColumnOrder,
} from './column-order-validation.js';

/**
 * Configuration hook for the DataTable ColumnOrder feature.
 * @internal
 */
export function useColumnOrder<TData extends DataTableRowData>(
  props: DataTableColumnOrderProps & {
    columns: DataTableColumnDef<TData>[];
  },
  options: DataTableOptions<Exclude<TData, unknown>>,
): void {
  // oxlint-disable-next-line react/immutability
  options.enableColumnOrdering = false;

  const { columnOrder: columnOrderFromDefinition, columnGroups } =
    extractColumnOrderAndGroups<TData>(props.columns);

  // save the last valid column order to be able to check the validity of a new one
  const lastValidColumnOrder = useRef<string[] | undefined>(
    columnOrderFromDefinition,
  );
  // save the column order inferred from the column definition to be able to check if it changed
  const lastColumnOrderFromDefinition = useRef<string[] | undefined>(
    columnOrderFromDefinition,
  );

  const [columnOrder, setColumnOrder] = useControllableState({
    value: props.columnOrder,
    // if no order was specified, initialize with column definition order
    defaultValue: props.defaultColumnOrder || columnOrderFromDefinition,
  });

  const onColumnOrderChangeProp = useCallbackRef(props.onColumnOrderChange);

  const onColumnOrderChangeCallback = useCallback(
    (
      updater: Updater<ColumnOrderState>,
      trigger: 'user' | 'internal' = 'user',
    ): void => {
      const nextState = functionalUpdate(
        updater,
        options.state?.columnOrder ?? columnOrder,
      );

      if (!isEqual(nextState, columnOrder)) {
        setColumnOrder(nextState);
        if (trigger !== 'internal') {
          onColumnOrderChangeProp(nextState, trigger);
        }
      }
    },
    [setColumnOrder, onColumnOrderChangeProp, columnOrder, options],
  );

  // Apply internal corrections (invalid order, or column definition changes). Calling
  // setColumnOrder in render would notify every store subscriber synchronously,
  // possibly causing "Maximum update depth exceeded" errors.
  // useLayoutEffect runs before paint, so the brief invalid order is never visible.
  useLayoutEffect(() => {
    if (!props.columnOrdering) {
      return;
    }

    // Column definitions changed while uncontrolled — adopt the new order.
    if (
      props.columnOrder === undefined &&
      !isEqual(lastColumnOrderFromDefinition.current, columnOrderFromDefinition)
    ) {
      lastColumnOrderFromDefinition.current = columnOrderFromDefinition;
      setColumnOrder(columnOrderFromDefinition);
      onColumnOrderChangeProp(columnOrderFromDefinition, 'internal');
      return;
    }

    // Validate the column order when the current order is not equal
    // with the last valid order or if the current order has different
    // elements than the order extracted from the column definition.
    if (!isEqual(columnOrder, lastValidColumnOrder.current)) {
      const validColumnOrder = validateColumnOrder(
        columnOrder,
        columnOrderFromDefinition,
        columnGroups,
      );
      if (validColumnOrder === null) {
        // Order is valid — track it so the same order does not get re-validated.
        lastValidColumnOrder.current = columnOrder;
      } else {
        // Track the corrected order, so the same order does not get re-validated.
        // Only notify if the order was invalid and has been corrected.
        lastValidColumnOrder.current = validColumnOrder;
        setColumnOrder(validColumnOrder);
        onColumnOrderChangeProp(validColumnOrder, 'internal');
      }
    }
  });

  if (!props.columnOrdering) {
    return;
  }

  // oxlint-disable-next-line react/immutability
  options.enableColumnOrdering = true;
  // oxlint-disable-next-line react/immutability
  options.state = {
    ...options.state,
    columnOrder,
  };

  // oxlint-disable-next-line react/immutability
  options.onColumnOrderChange = onColumnOrderChangeCallback;

  // oxlint-disable-next-line react/immutability
  options.originalColumnOrder =
    props.defaultColumnOrder ?? lastValidColumnOrder.current;
}
